import bxRightArrowAlt from '@iconify-icons/bx/bx-right-arrow-alt';
import { Icon } from '@iconify/react';
import { ClassroomType } from '@peekapak/types/AnalyticsTypes';
import { ELAStandardsMap, ModuleCompletionStatus } from '@peekapak/types/LessonPlanTypes';
import { TakweenKey } from '@peekapak/types/DataProtocolTypes';
import cx from 'classnames';
import { cloneDeep } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Analytics from '../Analytics';
import { RootState } from '../ApplicationState';
import { getELAStandardsMap, updateUserProfile } from '../BackendInterface';
import { selectClassroom } from '../Classrooms';
import Button from '../components/Button';
import CTASegment, { Heading } from '../components/CTASegment';
import LibraryBanner from '../components/LibraryBanner';
import {
  AllCardData,
  ElementaryCard,
  getElementaryFreemiumUnits,
  getElementarySchoolUnits,
  getHighSchoolUnits,
  getLinkCards,
  getMiddleSchoolUnits,
  getTakweenUnits,
  getVideoCards,
  highSchoolFreemiumModules,
  languageCard,
  LinkCard,
  MiddleSchoolCard,
  middleSchoolFreemiumModules,
  myPeekavillePlaceholderCard,
} from '../components/LibraryCard/cardData';
import ElementaryLibraryCard from '../components/LibraryCard/ElementaryLibraryCard';
import LanguageLibraryCard from '../components/LibraryCard/LanguageLibraryCard';
import LessonLibraryCard from '../components/LibraryCard/LessonLibraryCard';
import LinkLibraryCard from '../components/LibraryCard/LinkLibraryCard';
import MiddleSchoolLibraryCard from '../components/LibraryCard/MiddleSchoolLibraryCard';
import VideoLibraryCard from '../components/LibraryCard/VideoLibraryCard';
import LibraryFilters from '../components/LibraryFilters';
import { HIDE_FILTERS_FOR_LICENSE } from '../components/LibraryFilters/filterData';
import { createOrUpdateUserProfileFlag, Segment } from '../core/core';
import {
  getGradeAsNumber,
  getLetterGradeFromNumberGrade,
  getUnitTitleForDisplay,
  getUsersEnableContentTags,
  isUserHasKey,
} from '../GlobalFunctions';
import libraryStyles from '../SCSS/Library.module.scss';
import { unsetUnit } from '../Session';
import { updateAllFields } from '../UserManagement';
import { countCompletionStatuses } from './../completionStatus';
import utilityStyles from './../SCSS/common/utilityStyles.module.scss';
import styles from './Library.module.scss';
import {
  getEffectiveActiveLibrary,
  hasBuildingResilienceAccess,
  hasFutureReadyAccess,
  hasPeekapakLibraryAccess,
  isNewTakweenExperienceEnabled,
  isTakweenUnitAccessible,
  shouldShowLibrarySwitcher,
} from './takweenLibraryAccess';

interface Props {
  grade: number;
}

interface UnitProps {
  [key: string]: number;
}

const ElementaryFreemiumUnitOrderMap: UnitProps = {
  'Self-Regulation': 1,
  Respect: 2,
  Gratitude: 3,
  Kindness: 4,
  Perseverance: 5,
  Teamwork: 6,
  Empathy: 7,
  Honesty: 8,
  Optimism: 9,
  Courage: 10,
};

const MiddleSchoolFreemiumUnitOrderMap: UnitProps = {
  welcome: 1,
  'identity-standard': 2,
  stress: 3,
  decision: 4,
  belonging: 5,
  empathy: 6,
};

const HighSchoolFreemiumUnitOrderMap: UnitProps = {
  hswelcome: 1,
  'hsidentity-standard': 2,
  hsstress: 3,
  hsdecision: 4,
  hsbelonging: 5,
  hsempathy: 6,
};

const LIBRARY_DISPLAY_NAME: Record<'peekapak' | 'takween', string> = {
  peekapak: 'Peekapak Library',
  takween: 'Takween Al Ghurair Program',
};

const Library = ({ grade }: Props): JSX.Element => {
  const isBasicUser = !isUserHasKey('KEY_CHANGE_GRADE_LEVEL');
  const initialFilterArray = isBasicUser ? ['freemium'] : [''];
  const initialBannerFilter = isBasicUser ? 'freemium' : '';

  const [filterArray, setFilterArray] = useState(initialFilterArray);
  const [bannerFilter, setBannerFilter] = useState(initialBannerFilter);
  const [activeLibrary, setActiveLibrary] = useState<'peekapak' | 'takween'>(() => getEffectiveActiveLibrary());
  const [isShowBanner, setShowBanner] = useState(true);
  const [displayArray, setDisplayArray] = useState<AllCardData[]>([]);
  const [curriculumType, setCurriculumType] = useState('');
  const [cardQty, setCardQty] = useState(15);
  const [isExpanded, setIsExpanded] = useState(false);
  const [standardsMap, setStandardsMap] = useState<ELAStandardsMap | null>(null);

  const handleShowMore = () => {
    setIsExpanded(!isExpanded);
    setCardQty(isExpanded ? 15 : displayArray.length);
  };

  const dispatch = useDispatch();

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });

  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });
  const alternateLibrary = activeLibrary === 'peekapak' ? 'takween' : 'peekapak';
  // Computed once per render and reused below - depends only on hostname + Redux user
  // state, both stable for the duration of a render, so there's no need to re-derive it
  // at every call site (previously called 7x per render, including once per Takween unit).
  const newTakweenExperienceEnabled = isNewTakweenExperienceEnabled();

  const getMiddleSchoolPercentComplete = (id: string, numModules: number) => {
    if (!classroom?.completionStatus?.[id]) return 0;

    const unitCompletion = classroom.completionStatus[id];

    let totalLessons = numModules;
    if (standardsMap?.data?.[id]) {
      totalLessons = Object.keys(standardsMap.data[id]).length;
    }
    if (totalLessons === 0) return 0;

    const completeLessons = Object.values(unitCompletion).filter((lessonCompletion) => {
      const moduleStatuses = Object.values(lessonCompletion as ModuleCompletionStatus);
      return moduleStatuses.length > 0 && moduleStatuses.every((v) => v === 'Completed' || v === 'DeemedCompleted');
    }).length;

    return Math.round((completeLessons / totalLessons) * 100);
  };

  const calculateElementaryCompletion = (unit: string) => {
    if (parseInt(grade) > 5) return 0;
    const myCompletionStatusKey = `${unit}-${getLetterGradeFromNumberGrade(grade)}`;
    const myCompletionStatus = classroom?.completionStatus?.[myCompletionStatusKey];
    const myStatuses = countCompletionStatuses(myCompletionStatus);
    const percentage = Math.round((myStatuses.Completed / myStatuses.Total) * 100);
    return percentage;
  };

  const updateFilters = (filterValue: string) => {
    if (filterValue === initialBannerFilter) {
      setFilterArray(initialFilterArray);
      setBannerFilter(initialBannerFilter);
      return;
    }
    if (filterArray.includes(filterValue)) {
      const newFilterArray = filterArray.filter((item) => item !== filterValue);
      if (newFilterArray.length === 0) {
        setFilterArray(initialFilterArray);
      } else {
        setFilterArray(newFilterArray);
      }
      if (newFilterArray.length === 1) {
        setBannerFilter(newFilterArray[0]);
      } else {
        setBannerFilter(initialBannerFilter);
      }
      return;
    }
    const newFilterArray = [...filterArray, filterValue];
    const filterArrayWithAllRemoved = newFilterArray.filter((item) => item !== initialBannerFilter);
    setFilterArray(filterArrayWithAllRemoved);
    setBannerFilter(filterValue);
  };

  const toggleBanner = async (isShow: boolean) => {
    setShowBanner(isShow);
    if (userProfile) {
      const newUserProfile = createOrUpdateUserProfileFlag('isShowLibraryBanner', isShow, userProfile);
      dispatch(updateAllFields(newUserProfile));
      await updateUserProfile({
        newUserData: {
          flags: { ...userProfile.flags, isShowLibraryBanner: isShow },
        },
      });
    }
  };

  const handleChangeFilter = (filterValue: string) => {
    updateFilters(filterValue);
    getDisplayOrder(filterValue === '' ? initialArray : displayArray, filterValue);
  };

  const getDisplayOrder = (inputArray: AllCardData[], filterValue: string) => {
    const isTrialUser = userProfile?.licenseLevel.includes('TRIAL');
    if (isTrialUser) {
      const wrongGradeLast = [
        ...inputArray.filter((x) => !x.isWrongGrade),
        ...inputArray.filter((x) => x.isWrongGrade),
      ];
      setDisplayArray(wrongGradeLast);
      return;
    }

    const wrongGradeLast = [...inputArray.filter((x) => !x.isWrongGrade), ...inputArray.filter((x) => x.isWrongGrade)];
    const lockedLast = [...wrongGradeLast.filter((x) => !x.isLocked), ...wrongGradeLast.filter((x) => x.isLocked)];
    if (filterValue === '') {
      setDisplayArray(lockedLast);
      return;
    }
    const mostRecentFilterFirst = [
      ...lockedLast.filter((x) => x.filterTags.includes(filterValue)),
      ...lockedLast.filter((x) => !x.filterTags.includes(filterValue)),
    ];
    setDisplayArray(mostRecentFilterFirst);
    return;
  };

  const getLockedUnits = (isTrialable: boolean | undefined) => {
    if (userProfile?.licenseLevel.includes('TRIAL')) return isTrialable ? false : true;
    if (isUserHasKey('KEY_ALL_LESSON_PLANS') || isUserHasKey('KEY_MANAGE_STUDENTS')) return false;
    else return true;
  };

  const getWrongGradeUnits = (gradeSpan: number[]) => {
    const gradeAsNumber = getGradeAsNumber(classroom.grade);
    if (!gradeSpan.includes(gradeAsNumber)) return true;
    return false;
  };

  const populateCardPropertiesBasedOnState = () => {
    // Early check for Takween users - bypass normal content filtering
    if (
      newTakweenExperienceEnabled
        ? activeLibrary === 'takween' && (hasBuildingResilienceAccess() || hasFutureReadyAccess())
        : userProfile?.licenseLevel === 'TAKWEEN'
    ) {
      // Get ALL Takween units (both middle and high school)
      const allTakweenUnits = [...getTakweenUnits()];

      // Apply standard locked/grade logic to Takween units - key-based per-unit locking only
      // applies under the new switcher; the licenseLevel fallback matches old behavior, where
      // Takween users had full access to their content (isLocked: false), unconditionally.
      const processedUnits = allTakweenUnits.map((unit) => {
        const isFutureReadyPlaceholder = unit.requiredKey === TakweenKey.TAKWEEN_FUTURE_READY && unit.isUnavailable;
        return {
          ...unit,
          isLocked: newTakweenExperienceEnabled
            ? !isTakweenUnitAccessible(unit.requiredKey, getGradeAsNumber(classroom.grade))
            : false,
          isWrongGrade: getWrongGradeUnits(unit.gradeSpan || []),
          isComingSoon: isFutureReadyPlaceholder && hasFutureReadyAccess(),
        };
      });

      // Return ONLY Takween units, no extras, no videos, no links
      return processedUnits as AllCardData[];
    }

    // Regular flow for non-Takween users
    const unitCards: (ElementaryCard | MiddleSchoolCard | LinkCard)[] = [
      ...getElementarySchoolUnits(),
      ...getMiddleSchoolUnits(),
      ...getHighSchoolUnits(),
    ];
    const lockedStatusUnits = unitCards.map((x) => {
      return {
        ...x,
        isLocked: getLockedUnits('isTrialable' in x && x.isTrialable ? x.isTrialable : false),
      };
    });
    const wrongGradeStatusUnits = lockedStatusUnits.map((x) => {
      return {
        ...x,
        isWrongGrade: getWrongGradeUnits('gradeSpan' in x && x.gradeSpan ? x.gradeSpan : []),
      };
    });
    const lockedStatusVideoLessons = getVideoCards().map((x) => {
      return {
        ...x,
        isLocked: 'isTrialable' in x && x.isTrialable ? false : !isUserHasKey('KEY_VIDEO_LESSONS'),
        isWrongGrade: getWrongGradeUnits(x.gradeSpan ? x.gradeSpan : []),
      };
    });

    const contentFilteredLinkCards = getLinkCards();
    const wrongGradeLinkCards = contentFilteredLinkCards.map((x) => {
      const isPeekavilleIntro = x.displayName === 'Intro to myPeekaville';
      const isBasic = !isUserHasKey('KEY_ALL_LESSON_PLANS') && !isUserHasKey('KEY_MANAGE_STUDENTS');
      return {
        ...x,
        isWrongGrade: isPeekavilleIntro && classroom.grade > 5,
        isLocked: isPeekavilleIntro && isBasic,
      };
    });
    const visibleLanguageCard = languageCard.map((x) => {
      const enabledContentTags = getUsersEnableContentTags();
      return {
        ...x,
        isLocked: !isUserHasKey('KEY_MANAGE_CLASSROOMS') || (enabledContentTags && enabledContentTags.includes('mena')),
        isWrongGrade: getWrongGradeUnits(x.gradeSpan),
      };
    });
    const allTheCards: AllCardData[] = [
      ...wrongGradeStatusUnits,
      ...visibleLanguageCard,
      ...lockedStatusVideoLessons,
      ...wrongGradeLinkCards,
    ];

    return allTheCards;
  };
  const initialArray = populateCardPropertiesBasedOnState();

  const isFreemium = filterArray.includes('freemium');

  const getElementaryFreemiumUnitSubtitle = (unit: string) => {
    const unitTitle = getUnitTitleForDisplay(unit); // remove 'Mena' if present
    return `Unit ${ElementaryFreemiumUnitOrderMap[unitTitle]}: ${unitTitle}`;
  };

  const getElementaryFreeResources = () => {
    // get all freemium cards from the initial array
    const initialFreemiumArray = [
      ...initialArray.filter((item) => item.filterTags.includes('freemium')),
      ...(getElementaryFreemiumUnits() as AllCardData[]),
    ];
    // organize cards by unit; add myPeekaville placegholder card at the end of each unit; give each card an order
    const freemiumCategoriedByUnit = Object.keys(ElementaryFreemiumUnitOrderMap).reduce((acc, unit) => {
      const myPeekavillePlaceholderData = cloneDeep(myPeekavillePlaceholderCard);
      myPeekavillePlaceholderData[0].unit = unit;
      const unitData = [
        ...initialFreemiumArray.filter(
          (item) => (item.unit && getUnitTitleForDisplay(item.unit) === unit) || item.displayName === unit,
        ),
        ...myPeekavillePlaceholderData,
      ];
      for (let i = 0; i < unitData.length; i++) {
        const card = unitData[i];
        if (card.label === 'Letter') {
          card.order = 2;
        } else if (card.label === 'Storybook') {
          card.order = 3;
        } else if (card.type === 'link') {
          card.order = 4;
        } else if (card.label === 'myPeekaville') {
          card.order = 5;
        } else {
          card.order = 1;
        }
      }
      const sortedUnitData = unitData.sort((a, b) => a.order! - b.order!);
      return acc.concat(sortedUnitData);
    }, [] as AllCardData[]);
    // get howto and extras cards
    const howToAndExtras = [
      ...initialArray.filter((item) => item.filterTags.includes('howTo')),
      ...initialArray.filter((item) => item.filterTags.includes('extras')),
    ];
    // add howTo and extras cards
    const finalizedFreemiumArray = freemiumCategoriedByUnit.concat(howToAndExtras);
    setDisplayArray(finalizedFreemiumArray);
  };

  const getMiddleSchoolFreeResources = () => {
    const initialFreemiumArray = [
      ...initialArray.filter((item) => item.filterTags.includes('freemium')),
      ...middleSchoolFreemiumModules,
    ];
    const freemiumCategoriedByUnit = Object.keys(MiddleSchoolFreemiumUnitOrderMap).reduce((acc, unit) => {
      const unitData = [...initialFreemiumArray.filter((item) => item.unit === unit)];
      for (let i = 0; i < unitData.length; i++) {
        const card = unitData[i];
        if (card.label === 'Activity') {
          if (card.moduleIndex === 1) {
            card.order = 2;
          }
          if (card.moduleIndex === 2) {
            card.order = 3;
          }
        } else if (card.type === 'link') {
          card.order = 4;
        } else {
          card.order = 1;
        }
      }
      const sortedUnitData = unitData.sort((a, b) => a.order! - b.order!);
      return acc.concat(sortedUnitData);
    }, [] as AllCardData[]);
    // get poster, howto and extras cards
    const otherCards = [
      ...initialArray.filter((item) => item.filterTags.includes('posters') && !item.unit),
      ...initialArray.filter((item) => item.filterTags.includes('howTo')),
      ...initialArray.filter((item) => item.filterTags.includes('extras')),
    ];
    // combine freemium cards(unit, lesson, poster) with howTo and extras cards
    const finalizedFreemiumArray = freemiumCategoriedByUnit.concat(otherCards);
    setDisplayArray(finalizedFreemiumArray);
  };

  const getHighSchoolFreeResources = () => {
    const initialFreemiumArray = [
      ...initialArray.filter((item) => item.filterTags.includes('freemium')),
      ...highSchoolFreemiumModules,
    ];

    // get poster cards for high school units
    const posterCards = [
      ...initialArray.filter((item) => item.filterTags.includes('posters') && item?.gradeSpan?.includes(9)),
    ].map((card) => ({ ...card, unit: 'hs' + card.unit }));

    const freemiumCategoriedByUnit = Object.keys(HighSchoolFreemiumUnitOrderMap).reduce((acc, unit) => {
      const unitData = [
        ...initialFreemiumArray.filter((item) => item.unit === unit),
        ...posterCards.filter((item) => item.unit === unit),
      ];

      for (let i = 0; i < unitData.length; i++) {
        const card = unitData[i];
        if (card.label === 'Activity') {
          if (card.moduleIndex === 1) {
            card.order = 2;
          }
          if (card.moduleIndex === 2) {
            card.order = 3;
          }
        } else if (card.type === 'link') {
          card.order = 4;
        } else {
          card.order = 1;
        }
      }
      const sortedUnitData = unitData.sort((a, b) => a.order! - b.order!);
      return acc.concat(sortedUnitData);
    }, [] as AllCardData[]);

    // get howto and extras cards
    const otherCards = [
      ...initialArray.filter((item) => item.filterTags.includes('howTo')),
      ...initialArray.filter((item) => item.filterTags.includes('extras')),
    ];
    // combine freemium cards(unit, lesson, poster) with howTo and extras cards
    const finalizedFreemiumArray = freemiumCategoriedByUnit.concat(otherCards);
    setDisplayArray(finalizedFreemiumArray);
  };

  useEffect(() => {
    dispatch(unsetUnit());
    getDisplayOrder(initialArray, '');
  }, [classroom, !isFreemium, activeLibrary]);

  // The freemium/filterArray system is a Peekapak-only concept - its tags
  // (elementary/middleSchool/freemium/etc.) never match Takween units' own
  // filterTags, so without this the units get silently filtered out of
  // display the moment isBasicUser (KEY_CHANGE_GRADE_LEVEL) is true.
  useEffect(() => {
    if (newTakweenExperienceEnabled && activeLibrary === 'takween') {
      setFilterArray(['']);
    } else {
      setFilterArray(isBasicUser ? ['freemium'] : ['']);
    }
  }, [activeLibrary, isBasicUser, newTakweenExperienceEnabled]);

  useEffect(() => {
    if (classroom.grade > 5 && classroom.grade < 9) {
      setCurriculumType('MS');
    } else if (classroom.grade > 8) {
      setCurriculumType('HS');
    } else {
      setCurriculumType('EL');
    }
  }, [classroom]);

  useEffect(() => {
    if (!curriculumType || !userProfile) return;
    Analytics.libraryViewedEvent(
      activeLibrary,
      curriculumType as ClassroomType,
      hasPeekapakLibraryAccess() === true,
      userProfile,
    ).catch((error) => console.error('[Analytics] libraryViewedEvent failed:', error));

    // 'EL' is exactly grade <= 5, Takween's grade floor - every unit card is locked here.
    if (activeLibrary === 'takween' && curriculumType === 'EL') {
      Analytics.libraryAccessBlockedEvent(
        'takween',
        curriculumType as ClassroomType,
        hasPeekapakLibraryAccess() === true,
        userProfile,
      ).catch((error) => console.error('[Analytics] libraryAccessBlockedEvent failed:', error));
    }
    // userProfile omitted - its object reference changes on unrelated updates (e.g. toggleBanner)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLibrary, curriculumType]);

  useEffect(() => {
    if (grade) {
      getELAStandardsMap(String(grade))
        .then(setStandardsMap)
        .catch(() => {});
    }
  }, [grade]);

  // get freemium contents for FREE RESOURCES filter tab. classroom is a dep
  // because the effect above resets displayArray on every classroom change;
  // switching within the same grade band leaves curriculumType unchanged, so
  // without it the freemium cards are dropped until reload. Must stay declared
  // after that effect so it runs second.
  useEffect(() => {
    if (curriculumType === 'EL' && isFreemium) {
      getElementaryFreeResources();
    } else if (curriculumType === 'MS' && isFreemium) {
      getMiddleSchoolFreeResources();
    } else if (curriculumType === 'HS' && isFreemium) {
      getHighSchoolFreeResources();
    }
  }, [classroom, isFreemium, curriculumType]);

  return (
    <div className={`${styles.Library} ${utilityStyles.clearfix}`}>
      <div className={styles.libraryTitle}>
        {newTakweenExperienceEnabled ? LIBRARY_DISPLAY_NAME[activeLibrary] : 'Library'}
      </div>

      {newTakweenExperienceEnabled && shouldShowLibrarySwitcher() && (
        <div className={styles.librarySwitcher}>
          <span className={cx(styles.activeLabel, { [styles.takween]: activeLibrary === 'takween' })}>
            <span className={styles.activeText}>
              {activeLibrary === 'takween' && (
                <img
                  src='/images/logos/AGF-logo_icon.png'
                  alt=''
                  width={12}
                  height={12}
                  className={styles.activeIcon}
                />
              )}
              {activeLibrary === 'peekapak' && <span className={styles.peekapakIcon}>P</span>}
              {LIBRARY_DISPLAY_NAME[activeLibrary]}
            </span>
            <span className={styles.activeBadge}>ACTIVE</span>
          </span>
          <Button
            small
            className={styles.switcherButton}
            onClick={() => {
              if (userProfile) {
                Analytics.librarySwitchedEvent(
                  activeLibrary,
                  alternateLibrary,
                  curriculumType as ClassroomType,
                  userProfile,
                ).catch((error) => console.error('[Analytics] librarySwitchedEvent failed:', error));
              }
              setActiveLibrary(alternateLibrary);
            }}
          >
            Go to {LIBRARY_DISPLAY_NAME[alternateLibrary]}&nbsp;&nbsp;
            <Icon
              icon={bxRightArrowAlt}
              width={16}
              height={16}
              color='black'
              style={{ position: 'relative', top: '-2px' }}
            />
          </Button>
        </div>
      )}

      <LibraryBanner
        level={curriculumType}
        bannerFilter={bannerFilter}
        toggleBanner={toggleBanner}
        isShowBanner={isShowBanner}
        classroom={classroom}
        handleClickLibraryFilter={handleChangeFilter}
        userProfile={userProfile}
        activeLibrary={activeLibrary}
      />
      {userProfile?.licenseLevel &&
        !HIDE_FILTERS_FOR_LICENSE.includes(userProfile.licenseLevel) &&
        !(newTakweenExperienceEnabled && activeLibrary === 'takween') && (
          <LibraryFilters
            filterArray={filterArray}
            handleChangeFilter={handleChangeFilter}
            isBasicUser={isBasicUser}
            userProfile={userProfile}
            classroomGrade={getGradeAsNumber(classroom.grade)}
          />
        )}
      {!userProfile?.licenseLevel && !(newTakweenExperienceEnabled && activeLibrary === 'takween') && (
        <LibraryFilters
          filterArray={filterArray}
          handleChangeFilter={handleChangeFilter}
          isBasicUser={isBasicUser}
          userProfile={userProfile}
          classroomGrade={getGradeAsNumber(classroom.grade)}
        />
      )}
      <Segment>
        <div className={styles.libraryCardContainer}>
          {(isFreemium && !isExpanded ? displayArray.slice(0, cardQty) : displayArray).map((card, index) => {
            const {
              filterTags,
              type,
              isLocked,
              isWrongGrade,
              hasSpanishContent,
              displayName,
              displayNameSpanish,
              content,
              contentSpanish,
              subtitle,
            } = card;
            const hasSpanishLibraryCardContent = classroom.language === 'es' && hasSpanishContent;
            const modifiedUnitForUnitLocks = card.unit && card.unit.replace('-', ' ');
            return (
              <Fragment key={`library card #${index}`}>
                {type === 'elementary' &&
                  card.unit &&
                  modifiedUnitForUnitLocks &&
                  classroom &&
                  filterArray.some((tag) => filterTags.includes(tag) || filterArray.includes('')) && (
                    <ElementaryLibraryCard
                      isShow={(curriculumType === 'EL' && isFreemium) || !isFreemium}
                      gradeSpan={card.gradeSpan}
                      hasSpanishContent={card.hasSpanishContent}
                      hasSpanishLibraryCardContent={hasSpanishLibraryCardContent}
                      isLocked={isLocked || false}
                      displayName={hasSpanishLibraryCardContent ? displayNameSpanish : displayName}
                      content={hasSpanishLibraryCardContent ? contentSpanish : content}
                      subtitle={isFreemium ? getElementaryFreemiumUnitSubtitle(card.unit) : subtitle}
                      unit={card.unit}
                      isWrongGrade={isWrongGrade}
                      percentComplete={calculateElementaryCompletion(card.unit)}
                      key={`${displayName} + ${index}`}
                      classroom={classroom}
                      hasPeekavilleContent={card.hasPeekavilleContent}
                      featuredImage={card.featuredImage}
                      isPeekavilleLocked={
                        !classroom.peekavilleUnitLocks ||
                        classroom.peekavilleUnitLocks[modifiedUnitForUnitLocks] === undefined ||
                        classroom.peekavilleUnitLocks[modifiedUnitForUnitLocks] === true
                      }
                    />
                  )}
                {type === 'lesson' &&
                  card.unit &&
                  classroom &&
                  filterArray.some(
                    (tag) =>
                      filterTags.includes(tag) &&
                      isFreemium &&
                      !isLocked &&
                      !isWrongGrade &&
                      filterTags.includes('freemium'),
                  ) && (
                    <LessonLibraryCard
                      isShow={(curriculumType === 'EL' && isFreemium) || !isFreemium}
                      key={`${displayName} + ${index}`}
                      displayName={displayName}
                      content={content}
                      subtitle={subtitle}
                      index={card.index}
                      label={card.label || ''}
                      buttonText={card.buttonText}
                      classroom={classroom}
                      unit={card.unit}
                      slug={card.slug}
                      featuredImage={card.featuredImage}
                    />
                  )}
                {type === 'module' &&
                  card.unit &&
                  classroom &&
                  filterArray.some(
                    (tag) =>
                      filterTags.includes(tag) &&
                      isFreemium &&
                      !isLocked &&
                      !isWrongGrade &&
                      filterTags.includes('freemium'),
                  ) && (
                    <LessonLibraryCard
                      isShow={((curriculumType === 'MS' || curriculumType === 'HS') && isFreemium) || !isFreemium}
                      key={`${displayName} + ${index}`}
                      displayName={displayName}
                      content={content}
                      subtitle={subtitle}
                      index={card.index}
                      label={card.label || ''}
                      classroom={classroom}
                      unit={card.unit}
                      lessonIndex={card.lessonIndex}
                      moduleIndex={card.moduleIndex}
                      lessonType={card.lessonType}
                      slug={card.slug}
                      featuredImage={card.featuredImage}
                    />
                  )}
                {type === 'placeholder' &&
                  classroom &&
                  filterArray.some(
                    (tag) =>
                      filterTags.includes(tag) &&
                      isFreemium &&
                      !isLocked &&
                      !isWrongGrade &&
                      filterTags.includes('freemium'),
                  ) && (
                    <LinkLibraryCard
                      isLocked={true}
                      isWrongGrade={isWrongGrade || false}
                      isUpgradable={true}
                      featuredImage={card.featuredImage}
                      displayName={displayName}
                      buttonText={card.buttonText || ''}
                      content={`Students play a variety of games to practice ${card.unit} skills on myPeekaville.com.`}
                      subtitle={`${card.unit} ${subtitle}`}
                      slug={card.slug || ''}
                      key={`${displayName} + ${index}`}
                      classroom={classroom}
                      isShow={card.gradeSpan?.includes(parseInt(classroom.grade))}
                    />
                  )}
                {type === 'language' &&
                  classroom &&
                  !isLocked &&
                  !isWrongGrade &&
                  classroom.language !== 'es' &&
                  filterArray.some((tag) => filterTags.includes(tag) || filterArray.includes('')) && (
                    <LanguageLibraryCard />
                  )}
                {type === 'middleSchool' &&
                  card.unit &&
                  card.numModules &&
                  classroom &&
                  filterArray.some((tag) => filterTags.includes(tag) || filterArray.includes('')) && (
                    <MiddleSchoolLibraryCard
                      isShow={(curriculumType === 'MS' && isFreemium) || !isFreemium}
                      displayName={displayName}
                      gradeSpan={card.gradeSpan}
                      content={content}
                      subtitle={subtitle}
                      unit={card.unit}
                      featuredImage={card.featuredImage}
                      isLocked={isLocked || false}
                      isUnavailable={'isUnavailable' in card ? card.isUnavailable : false}
                      isComingSoon={'isComingSoon' in card ? card.isComingSoon : false}
                      isWrongGrade={isWrongGrade}
                      percentComplete={getMiddleSchoolPercentComplete(card.unit, card.numModules || 0)}
                      classroom={classroom}
                      key={`${displayName} + ${index}`}
                    />
                  )}
                {type === 'highSchool' &&
                  card.unit &&
                  card.numModules &&
                  classroom &&
                  filterArray.some((tag) => filterTags.includes(tag) || filterArray.includes('')) && (
                    <MiddleSchoolLibraryCard
                      isShow={(curriculumType === 'HS' && isFreemium) || !isFreemium}
                      displayName={displayName}
                      gradeSpan={card.gradeSpan}
                      content={content}
                      subtitle={subtitle}
                      unit={card.unit}
                      featuredImage={card.featuredImage}
                      isLocked={isLocked || false}
                      isUnavailable={'isUnavailable' in card ? card.isUnavailable : false}
                      isComingSoon={'isComingSoon' in card ? card.isComingSoon : false}
                      isWrongGrade={isWrongGrade}
                      percentComplete={getMiddleSchoolPercentComplete(card.unit, card.numModules || 0)}
                      classroom={classroom}
                      key={`${displayName} + ${index}`}
                    />
                  )}
                {type === 'video' &&
                  card.assetId &&
                  card.featuredImage &&
                  classroom &&
                  filterArray.some((tag) => filterTags.includes(tag) || filterArray.includes('')) && (
                    <VideoLibraryCard
                      assetId={card.assetId}
                      displayName={displayName}
                      content={content}
                      subtitle={subtitle}
                      isLocked={isLocked}
                      featuredImage={card.featuredImage}
                      key={`${displayName} + ${index}`}
                      isWrongGrade={isWrongGrade || false}
                      gradeSpan={card.gradeSpan}
                    />
                  )}
                {type === 'link' &&
                  card.buttonText &&
                  card.slug &&
                  classroom &&
                  filterArray.some(
                    (tag) =>
                      filterTags.includes(tag) ||
                      filterArray.includes('') ||
                      (isFreemium && !isLocked && !isWrongGrade && filterTags.includes('freemium')),
                  ) && (
                    <LinkLibraryCard
                      isLocked={isLocked}
                      isWrongGrade={isWrongGrade || false}
                      featuredImage={card.featuredImage}
                      displayName={displayName}
                      buttonText={card.buttonText}
                      content={content}
                      subtitle={subtitle}
                      slug={card.slug}
                      key={`${displayName} + ${index}`}
                      classroom={classroom}
                      isShow={card.gradeSpan?.includes(parseInt(classroom.grade))}
                    />
                  )}
              </Fragment>
            );
          })}

          <div className={styles.rowFill} />
          <div className={styles.rowFill} />
          <div className={styles.rowFill} />
          <div className={styles.rowFill} />
        </div>
      </Segment>
      {isFreemium ? (
        <div className={styles.showMoreButtonContainer}>
          <button className={libraryStyles.textLink} onClick={handleShowMore}>
            Show {isExpanded ? 'less' : 'more'}...
          </button>
        </div>
      ) : null}
      {isFreemium && (
        <CTASegment>
          <Heading>Want Full Access to Peekapak?</Heading>
        </CTASegment>
      )}
    </div>
  );
};

export default Library;
